import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, AuditLog } from '@/api/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { format } from 'date-fns';
import { FileText, Search, Filter, Eye } from 'lucide-react';

export function AdminLogsPage() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-audit-logs', page, actionFilter, entityTypeFilter],
    queryFn: () =>
      adminApi.getAuditLogs({
        page,
        pageSize: 50,
        action: actionFilter || undefined,
        entityType: entityTypeFilter || undefined,
      }),
  });

  const { data: actions } = useQuery({
    queryKey: ['admin-audit-actions'],
    queryFn: () => adminApi.getAuditActions(),
  });

  const { data: entityTypes } = useQuery({
    queryKey: ['admin-audit-entity-types'],
    queryFn: () => adminApi.getAuditEntityTypes(),
  });

  const getActionBadgeColor = (action: string) => {
    if (action.includes('Delete')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    if (action.includes('Update') || action.includes('Add')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    if (action.includes('Force') || action.includes('Rebuild')) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    if (action.includes('Impersonate')) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  };

  const formatJson = (json: string | undefined) => {
    if (!json) return null;
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Logs</h1>
          <p className="text-gray-500 dark:text-gray-400">Track all admin actions on the platform</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white text-sm"
              >
                <option value="">All Actions</option>
                {actions?.map((action) => (
                  <option key={action} value={action}>{action}</option>
                ))}
              </select>
            </div>
            <select
              value={entityTypeFilter}
              onChange={(e) => {
                setEntityTypeFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="">All Entity Types</option>
              {entityTypes?.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : data?.items.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No audit logs found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Timestamp</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Action</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Entity</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">IP Address</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((log) => (
                      <tr key={log.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                          {format(new Date(log.createdAt), 'MMM d, yyyy HH:mm:ss')}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                          {log.userEmail || 'System'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getActionBadgeColor(log.action)}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm text-gray-900 dark:text-white">{log.entityType}</p>
                            {log.entityId && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                {log.entityId.substring(0, 8)}...
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                          {log.ipAddress || '-'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {(log.oldValues || log.newValues) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedLog(log)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, data?.totalCount || 0)} of {data?.totalCount} logs
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={!data || page * 50 >= data.totalCount}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      <Modal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="Audit Log Details"
      >
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Action</p>
                <p className="font-medium text-gray-900 dark:text-white">{selectedLog.action}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Entity</p>
                <p className="font-medium text-gray-900 dark:text-white">{selectedLog.entityType}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">User</p>
                <p className="font-medium text-gray-900 dark:text-white">{selectedLog.userEmail || 'System'}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Timestamp</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {format(new Date(selectedLog.createdAt), 'PPpp')}
                </p>
              </div>
            </div>

            {selectedLog.oldValues && (
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Old Values</p>
                <pre className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-xs overflow-x-auto text-red-800 dark:text-red-300">
                  {formatJson(selectedLog.oldValues)}
                </pre>
              </div>
            )}

            {selectedLog.newValues && (
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">New Values</p>
                <pre className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-xs overflow-x-auto text-green-800 dark:text-green-300">
                  {formatJson(selectedLog.newValues)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
